/**
 * Personalização de canais — o modal de editar e o de criar, no mesmo ficheiro
 * porque partilham os campos e as constantes (presets de slowmode e de bitrate).
 *
 * Os campos dependem do tipo: um canal de texto tem NSFW e modo lento; um de voz
 * tem limite de utilizadores e bitrate; uma categoria só tem nome. Mostrar tudo
 * a toda a gente seria ruído — o formulário adapta-se ao que o canal é.
 *
 * Feedback imediato: em vez de esperar pelo eco do gateway, aplica-se o
 * `ChannelUpdate`/`ChannelCreate`/`ChannelDelete` localmente logo a seguir ao
 * pedido REST. O eco que chega depois é idempotente (mesma chave, mesmo valor).
 */

import { type Channel, type ChannelType, deserialize, Limits, Permission, serialize } from "@greenvoice/shared";
import { useState } from "react";
import { api } from "../lib/api";
import { can, selfPermissions } from "../store/selectors";
import { useStore } from "../store/store";
import { IconClose, IconLock, IconTrash } from "./Icons";

/** Presets de modo lento. O tecto (6h) é o do protocolo (`Limits.slowmodeSeconds.max`). */
const SLOWMODE_PRESETS: { value: number; label: string }[] = [
  { value: 0, label: "Desligado" },
  { value: 5, label: "5 segundos" },
  { value: 10, label: "10 segundos" },
  { value: 15, label: "15 segundos" },
  { value: 30, label: "30 segundos" },
  { value: 60, label: "1 minuto" },
  { value: 120, label: "2 minutos" },
  { value: 300, label: "5 minutos" },
  { value: 600, label: "10 minutos" },
  { value: 900, label: "15 minutos" },
  { value: 1800, label: "30 minutos" },
  { value: 3600, label: "1 hora" },
  { value: 7200, label: "2 horas" },
  { value: 21600, label: "6 horas" },
];

/** Presets de bitrate (bps). Menos = mais leve na rede; mais = melhor áudio. */
const BITRATE_PRESETS: { value: number; label: string }[] = [
  { value: 8000, label: "8 kbps · voz econômica" },
  { value: 16000, label: "16 kbps" },
  { value: 24000, label: "24 kbps" },
  { value: 32000, label: "32 kbps" },
  { value: 48000, label: "48 kbps" },
  { value: 64000, label: "64 kbps · padrão" },
  { value: 96000, label: "96 kbps" },
  { value: 128000, label: "128 kbps · alta" },
  { value: 160000, label: "160 kbps" },
  { value: 192000, label: "192 kbps" },
  { value: 256000, label: "256 kbps · máxima" },
];

const isTextual = (type: ChannelType) => type === "TEXT" || type === "ANNOUNCEMENT";

/**
 * Exportado por causa do `ThreadCreate`: um tópico é um canal, e o nome dele
 * passa pelo MESMO `Limits.channelName` do lado do servidor. Uma segunda cópia
 * desta função era uma cópia à espera de divergir da regra que a API aplica.
 */
export function nameError(name: string): string | null {
  const value = name.trim();
  if (value.length < Limits.channelName.min) return "O nome não pode ficar vazio.";
  if (value.length > Limits.channelName.max) return `Máximo ${Limits.channelName.max} caracteres.`;
  if (!Limits.channelName.pattern.test(value)) return "Sem espaços no início nem os símbolos # ou @.";
  return null;
}

/* ── editar ───────────────────────────────────────────────────── */

export function ChannelSettings({ channelId, onClose }: { channelId: string; onClose: () => void }) {
  const state = useStore();
  const channel = state.channels[channelId];
  const applyEvent = state.applyEvent;

  const [name, setName] = useState(channel?.name ?? "");
  const [topic, setTopic] = useState(channel?.topic ?? "");
  const [nsfw, setNsfw] = useState(channel?.nsfw ?? false);
  const [slowmode, setSlowmode] = useState(channel?.slowmodeSeconds ?? 0);
  const [userLimit, setUserLimit] = useState(channel?.userLimit ?? 0);
  const [bitrate, setBitrate] = useState(channel?.bitrate ?? Limits.voiceBitrate.default);

  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!channel) return null;

  const type = channel.type;
  // Capturado aqui, no fluxo síncrono onde o `channel` já está estreitado — o
  // narrowing do early-return não atravessa a closure async do `remove`.
  const parentServerId = channel.serverId;
  const isVoice = type === "VOICE";
  const isCategory = type === "CATEGORY";
  const invalidName = nameError(name);

  // Trancar = negar a @todos o bit de entrada: CONNECT numa sala de voz (vê-se
  // quem lá está mas não se entra) ou VIEW_CHANNEL nos restantes (fica privado,
  // escondido). Faz-se por overwrite da role @everyone — o mesmo mecanismo das
  // permissões por canal. Só se mostra a quem gere permissões E tem o próprio
  // bit (o backend recusa negar uma permissão que o actor não possua).
  const everyoneRole = parentServerId
    ? Object.values(state.roles).find((r) => r.serverId === parentServerId && r.isDefault)
    : undefined;
  const lockBit = isVoice ? Permission.CONNECT : Permission.VIEW_CHANNEL;
  const selfPerms = selfPermissions(state, parentServerId, channel);
  const canLock = Boolean(everyoneRole) && can(selfPerms, Permission.MANAGE_PERMISSIONS) && can(selfPerms, lockBit);
  const everyoneOverwrite = everyoneRole
    ? channel.overwrites.find((o) => o.targetType === "ROLE" && o.targetId === everyoneRole.id)
    : undefined;
  const currentAllow = everyoneOverwrite ? deserialize(everyoneOverwrite.allow) : 0n;
  const currentDeny = everyoneOverwrite ? deserialize(everyoneOverwrite.deny) : 0n;
  const locked = (currentDeny & lockBit) === lockBit;

  async function toggleLock() {
    if (!everyoneRole) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      let updated: Channel;
      if (locked) {
        // Destrancar: retira o bit do deny. Se o overwrite ficar vazio, apaga-se
        // — não deixamos um overwrite fantasma (allow=0/deny=0) a poluir o canal.
        const deny = currentDeny & ~lockBit;
        if (deny === 0n && currentAllow === 0n) {
          updated = await api.channels.clearOverwrite(channelId, everyoneRole.id);
        } else {
          updated = await api.channels.setOverwrite(channelId, everyoneRole.id, {
            targetType: "ROLE",
            allow: serialize(currentAllow),
            deny: serialize(deny),
          });
        }
      } else {
        // Trancar: mete o bit no deny e tira-o do allow — o backend recusa a
        // mesma permissão em allow e deny ao mesmo tempo.
        updated = await api.channels.setOverwrite(channelId, everyoneRole.id, {
          targetType: "ROLE",
          allow: serialize(currentAllow & ~lockBit),
          deny: serialize(currentDeny | lockBit),
        });
      }
      applyEvent({ type: "ChannelUpdate", channelId, channel: updated });
      setNotice(locked ? "Canal destrancado." : "Canal trancado.");
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (invalidName) {
      setError(invalidName);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    // Construído por tipo — mandar campos de voz num canal de texto (ou vice-versa)
    // só dava um 400. Cada campo tem valor concreto, sem opcionais a espalhar.
    const body: Partial<Pick<Channel, "name" | "topic" | "nsfw" | "slowmodeSeconds" | "userLimit" | "bitrate">> = {
      name: name.trim(),
    };
    if (!isCategory) body.topic = topic.trim() || null;
    if (isTextual(type)) {
      body.nsfw = nsfw;
      body.slowmodeSeconds = slowmode;
    }
    if (isVoice) {
      body.userLimit = userLimit;
      body.bitrate = bitrate;
    }
    try {
      const updated = await api.channels.update(channelId, body);
      applyEvent({ type: "ChannelUpdate", channelId, channel: updated });
      setNotice("Canal salvo.");
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api.channels.remove(channelId);
      applyEvent({ type: "ChannelDelete", channelId, serverId: parentServerId });
      onClose();
    } catch (caught) {
      setError((caught as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label={`Configurações de ${channel.name}`}>
      <div className="modal__panel settings__panel">
        <header className="modal__head">
          <h2>Editar canal</h2>
          <button type="button" className="icon-button" onClick={onClose} title="Fechar">
            <IconClose size={18} />
          </button>
        </header>

        <div className="settings__body">
          <form
            className="settings__form"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <label className="field">
              <span>Nome do canal</span>
              <input
                value={name}
                maxLength={Limits.channelName.max}
                aria-invalid={invalidName !== null}
                onChange={(e) => setName(e.target.value)}
              />
            </label>

            {!isCategory ? (
              <label className="field">
                <span>Tópico</span>
                <textarea
                  value={topic}
                  maxLength={Limits.channelTopic.max}
                  rows={3}
                  placeholder="Do que se fala aqui?"
                  onChange={(e) => setTopic(e.target.value)}
                />
              </label>
            ) : null}

            {isTextual(type) ? (
              <>
                <label className="field">
                  <span>Modo lento</span>
                  <select value={slowmode} onChange={(e) => setSlowmode(Number(e.target.value))}>
                    {SLOWMODE_PRESETS.map((preset) => (
                      <option key={preset.value} value={preset.value}>
                        {preset.label}
                      </option>
                    ))}
                  </select>
                  <p className="modal__hint">
                    Tempo mínimo entre mensagens de cada pessoa. Não afeta quem gerencia mensagens.
                  </p>
                </label>

                <label className="check">
                  <input type="checkbox" checked={nsfw} onChange={(e) => setNsfw(e.target.checked)} />
                  <span>Conteúdo sensível (avisa antes de entrar)</span>
                </label>
              </>
            ) : null}

            {isVoice ? (
              <>
                <label className="field">
                  <span>Limite de usuários</span>
                  <input
                    type="number"
                    min={Limits.voiceUserLimit.min}
                    max={Limits.voiceUserLimit.max}
                    value={userLimit}
                    onChange={(e) =>
                      setUserLimit(
                        Math.max(
                          Limits.voiceUserLimit.min,
                          Math.min(Limits.voiceUserLimit.max, Math.floor(Number(e.target.value) || 0)),
                        ),
                      )
                    }
                  />
                  <p className="modal__hint">0 = sem limite.</p>
                </label>

                <label className="field">
                  <span>Qualidade do áudio</span>
                  <select value={bitrate} onChange={(e) => setBitrate(Number(e.target.value))}>
                    {BITRATE_PRESETS.map((preset) => (
                      <option key={preset.value} value={preset.value}>
                        {preset.label}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            ) : null}

            {canLock ? (
              <div className="field">
                <span>{isVoice ? "Trancar sala" : "Tornar privado"}</span>
                <button
                  type="button"
                  className={`lock-toggle${locked ? " is-locked" : ""}`}
                  onClick={() => void toggleLock()}
                  disabled={busy}
                  aria-pressed={locked}
                >
                  <IconLock size={16} />
                  <span>
                    {locked
                      ? isVoice
                        ? "Trancada — só entra quem tiver permissão"
                        : "Privado — escondido de @todos"
                      : isVoice
                        ? "Aberta a todos"
                        : "Visível para todos"}
                  </span>
                </button>
                <p className="modal__hint">
                  {isVoice
                    ? "Nega conectar a @todos: continuam vendo quem está na sala, mas só entra quem tiver permissão."
                    : "Nega ver o canal a @todos: passa a vê-lo só quem tiver uma permissão explícita."}
                </p>
              </div>
            ) : null}

            <div className="settings__foot">
              <button type="submit" className="button button--primary" disabled={busy || invalidName !== null}>
                Salvar
              </button>
              {notice ? (
                <span className="settings__notice" role="status">
                  {notice}
                </span>
              ) : null}
            </div>

            {error ? (
              <p className="auth__error" role="alert">
                {error}
              </p>
            ) : null}
          </form>

          <div className="settings__danger">
            {confirmDelete ? (
              <div className="settings__confirm">
                <span>Excluir #{channel.name}? Não há como desfazer.</span>
                <div className="settings__confirm-actions">
                  <button type="button" className="button" onClick={() => setConfirmDelete(false)} disabled={busy}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="button button--small icon-button--danger"
                    onClick={() => void remove()}
                    disabled={busy}
                  >
                    <IconTrash size={16} /> Excluir mesmo
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="button icon-button--danger"
                onClick={() => setConfirmDelete(true)}
                disabled={busy}
              >
                <IconTrash size={16} /> Excluir canal
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── criar ────────────────────────────────────────────────────── */

const CREATABLE_TYPES: { value: ChannelType; label: string }[] = [
  { value: "TEXT", label: "Texto" },
  { value: "VOICE", label: "Voz" },
  { value: "ANNOUNCEMENT", label: "Anúncios" },
  { value: "CATEGORY", label: "Categoria" },
];

export function ChannelCreate({
  serverId,
  parentId,
  onClose,
}: {
  serverId: string;
  parentId?: string | null;
  onClose: () => void;
}) {
  const applyEvent = useStore((s) => s.applyEvent);
  const selectChannel = useStore((s) => s.selectChannel);
  // Ler o dict inteiro (referência estável) e derivar no render — um selector que
  // devolvesse um array novo a cada chamada dispara o loop de getSnapshot do zustand v5.
  const channels = useStore((s) => s.channels);
  const categories = Object.values(channels).filter((c) => c.serverId === serverId && c.type === "CATEGORY");

  const [name, setName] = useState("");
  const [type, setType] = useState<ChannelType>("TEXT");
  const [parent, setParent] = useState<string>(parentId ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invalidName = nameError(name);

  async function create() {
    if (invalidName) {
      setError(invalidName);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const channel = await api.servers.createChannel(serverId, {
        name: name.trim(),
        type,
        // Uma categoria não vive dentro de outra; para os restantes, só se houver escolha.
        ...(type !== "CATEGORY" && parent ? { parentId: parent } : {}),
      });
      applyEvent({ type: "ChannelCreate", channel });
      if (channel.type !== "CATEGORY") selectChannel(channel.id);
      onClose();
    } catch (caught) {
      setError((caught as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Criar canal">
      <div className="modal__panel">
        <header className="modal__head">
          <h2>Criar canal</h2>
          <button type="button" className="icon-button" onClick={onClose} title="Fechar">
            <IconClose size={18} />
          </button>
        </header>

        <form
          className="settings__form"
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          <div className="field">
            <span>Tipo</span>
            <div className="type-picker" role="radiogroup" aria-label="Tipo de canal">
              {CREATABLE_TYPES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={type === option.value}
                  className={`type-picker__option${type === option.value ? " is-active" : ""}`}
                  onClick={() => setType(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <label className="field">
            <span>Nome</span>
            <input
              value={name}
              autoFocus
              maxLength={Limits.channelName.max}
              placeholder={type === "VOICE" ? "Sala de voz" : "novo-canal"}
              aria-invalid={name.length > 0 && invalidName !== null}
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          {type !== "CATEGORY" && categories.length > 0 ? (
            <label className="field">
              <span>Categoria</span>
              <select value={parent} onChange={(e) => setParent(e.target.value)}>
                <option value="">Sem categoria</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <div className="settings__foot">
            <button type="submit" className="button button--primary" disabled={busy || invalidName !== null}>
              Criar
            </button>
          </div>

          {error ? (
            <p className="auth__error" role="alert">
              {error}
            </p>
          ) : null}
        </form>
      </div>
    </div>
  );
}
